// The platform-operator API (/api/superadmin/*). Shapes mirror the backend's
// hand-built payloads; nothing here is an entity.
import { apiClient, ApiHttpError, type ClubRoleName } from "./api-client";
import type { ApiBadgeAward, ApiBadgeDefinition, ApiOfficialProgramSummary, BadgeKind, BadgeResultInput, BadgeRevokeReason, UsageArea } from "../types/api-types";
import type { ThemeInfo, ThemeName } from "./theme";

export type OwnerState = "none" | "never_invited" | "invite_pending" | "invite_expired" | "active";

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface OwnerSummary {
  membershipId: number;
  userId: number;
  name: string;
  email: string | null;
  state: OwnerState;
  stateLabel: string;
  invitedAt: string | null;
  lastLoginAt: string | null;
}

export interface ClubRow {
  id: number;
  name: string;
  isDemo: boolean;
  createdAt: string | null;
  activeMembers: number;
  lastActivityAt: string | null;
  owner: OwnerSummary | null;
  state: OwnerState;
  needsAttention: boolean;
}

export interface AttentionItem {
  clubId: number;
  clubName: string;
  state: OwnerState;
  stateLabel: string;
  owner: { membershipId: number; name: string; email: string | null; canBeInvited: boolean } | null;
  since: string | null;
}

export interface Overview {
  clubCount: number;
  /** Owners who were sent a link that is still valid and have not used it yet. */
  pendingInvites: number;
  stats: {
    activeUsersLast7Days: number;
    activeClubsLast7Days: number;
    activitiesLast7Days: number;
    lastActivityAt: string | null;
  };
  attention: AttentionItem[];
  /** What the platform owes people about their data: requests waiting and how late, and unusual file access. */
  privacy?: {
    erasurePending: number;
    erasureOverdue: number;
    requestsOpen: number;
    requestsOverdue: number;
    /** People who opened an unusual number of personal files within the last hour. */
    unusualAccess: { name: string; opened: number }[];
    /** Names of the clubs whose owner has not reviewed the team's access for six months. */
    accessReviewOverdue?: string[];
  };
}

export interface WorkspacePerson {
  membershipId: number;
  userId: number;
  name: string;
  email: string | null;
  role: ClubRoleName;
  status: "PENDING" | "ACTIVE" | "REVOKED";
  signedIn: boolean;
  lastLoginAt: string | null;
  invitedAt: string | null;
  invitedBy: string | null;
  canBeInvited: boolean;
  canReceiveOwnership: boolean;
}

export interface JournalEntry {
  id: number;
  action: string;
  entityClass: string;
  entityLabel: string | null;
  entityId: string | null;
  changes: Record<string, { old?: unknown; new?: unknown }> | null;
  actor: { id: number | null; name: string; roles: string[] };
  club: { id: number; name: string } | null;
  loggedAt: string;
  /** Done by a super admin through break-glass access to the club: why they were there. */
  accessGrant?: { id: number; reason: string } | null;
}

/** A super admin's break-glass access to a club (see ClubAccessGrant). */
export interface ClubAccessGrant {
  id: number;
  club: { id: number; name: string };
  admin: { id: number; name: string };
  reason: string;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  active: boolean;
}

export interface ClubWorkspace {
  id: number;
  name: string;
  description: string | null;
  /** Public path of the uploaded logo, or null when the club has none. */
  logoUrl: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  /** IANA timezone of the dojo; null = application default. */
  timezone: string | null;
  /** Language of the club's emails to people who have not chosen one; null = application default. */
  defaultLanguage: string | null;
  /** From name of the club's emails; null = the platform sender's name. */
  mailFromName: string | null;
  /** From address of the club's emails; null = the platform sender's address. */
  mailFromAddress: string | null;
  isDemo: boolean;
  createdAt: string | null;
  owner: WorkspacePerson | null;
  ownerState: OwnerState;
  ownerStateLabel: string;
  admins: WorkspacePerson[];
  counts: Record<ClubRoleName, number>;
  recentActivity: JournalEntry[];
}

export interface SearchResults {
  clubs: { id: number; name: string }[];
  people: { id: number; name: string; email: string | null; isSuperAdmin: boolean; isDisabled: boolean; clubs: { name: string; role: ClubRoleName }[] }[];
}

export type SignalementStatus = "new" | "in_progress" | "resolved" | "dismissed";
export type SignalementCategory = "data" | "bug" | "content" | "other" | "privacy";
export type SignalementSubjectType = "program_item" | "flashcard" | "club" | "person" | "screen" | "grading_system";

export interface SignalementCounts {
  new: number;
  in_progress: number;
  resolved: number;
  dismissed: number;
  /** new + in progress: everything that still asks for a decision. */
  open: number;
  total: number;
}

/** A problem a member reported from inside the app, as the operator reads it. */
/** A person's request to have their account erased: the platform answers within a month of their confirmation. */
export interface ErasureRequestRow {
  id: number;
  status: "pending" | "completed" | "refused";
  /** Null once the account is erased. */
  person: { id: number; name: string; email: string | null } | null;
  confirmedAt: string | null;
  dueAt: string | null;
  overdue: boolean;
  handledAt: string | null;
  handledByName: string | null;
  note: string | null;
  /** What would stand in the way, only for a pending request. */
  clubs: string[];
  ownsClub: boolean;
  coachedSessions: number;
  /** Family forms filed with this email: the children's registrations stay with the club. */
  familyForms: number;
}

export interface SignalementRow {
  id: number;
  status: SignalementStatus;
  category: SignalementCategory;
  message: string | null;
  subjectType: SignalementSubjectType;
  subjectId: string | null;
  /** What was flagged, in the reporter's words ("O-goshi", "Ukemi · Chute"). */
  subjectLabel: string | null;
  /** The screen it was reported from, as an in-app path. */
  pageUrl: string | null;
  club: { id: number; name: string } | null;
  /** Snapshot: the club is named even after it is deleted. */
  clubName: string | null;
  reporter: { id: number | null; name: string; role: string | null; email: string | null };
  /** The operator's follow-up, never shown to clubs. */
  internalNote: string | null;
  handledBy: string | null;
  handledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SignalementPage {
  items: SignalementRow[];
  pagination: Pagination;
  counts: SignalementCounts;
}

export type PdfIntegrationRequestStatus = "new" | "in_progress" | "done" | "declined";

export interface PdfIntegrationRequestCounts {
  new: number;
  in_progress: number;
  done: number;
  declined: number;
  /** new + in progress: everything that still asks for a decision. */
  open: number;
  total: number;
}

/** A club asking for a federation's paper form to be integrated online. */
export interface PdfIntegrationRequestRow {
  id: number;
  status: PdfIntegrationRequestStatus;
  /** The activity the forms belong to ("judo", "taiso"), and its federation when it has one. */
  activityKey: string;
  /** The activity's name, resolved for the queue: a key is not something to read. */
  activityName: string;
  federationKey: string | null;
  message: string | null;
  club: { id: number; name: string } | null;
  /** Snapshot: the club is named even after it is deleted. */
  clubName: string;
  requester: { id: number | null; name: string; email: string | null };
  /** The forms the club sent: what the operator opens to integrate them. */
  files: { name: string; url: string }[];
  /** The operator's answer, which the club reads once the request is answered. */
  note: string | null;
  handledBy: string | null;
  handledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PdfIntegrationRequestPage {
  items: PdfIntegrationRequestRow[];
  pagination: Pagination;
  counts: PdfIntegrationRequestCounts;
}

export interface PersonDetail {
  id: number;
  /** "First Last", for display. */
  name: string;
  firstName: string;
  lastName: string;
  email: string | null;
  isSuperAdmin: boolean;
  /** Set when a super admin disabled the account: no sign-in in any club. */
  disabledAt: string | null;
  /** Set when the person asked to restrict the use of their data: no emails to or about them. */
  processingRestrictedAt: string | null;
  createdAt: string | null;
  lastLoginAt: string | null;
  /** The person's own last use of the app; null until they use it. */
  lastActivityAt: string | null;
  /** The screen they were on then; null for accounts last seen before it existed. */
  lastActivityArea: UsageArea | null;
  memberships: {
    membershipId: number;
    club: { id: number; name: string };
    role: ClubRoleName;
    status: "PENDING" | "ACTIVE" | "REVOKED";
    invitedAt: string | null;
    canBeInvited: boolean;
    canViewAs: boolean;
  }[];
}

export interface NewClub {
  name: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  ownerEmail?: string;
  sendInvite?: boolean;
  allowDuplicateName?: boolean;
  demoSeed?: boolean;
  defaultProgram?: string;
  locale?: "fr" | "en";
}

export interface CreatedClub {
  club: ClubWorkspace;
  ownerHadAccount?: boolean;
  ownerOtherClubs?: number;
  invite?: { requested: boolean; status: "sent" | "failed" | "not_requested"; message: string | null };
  seedMode?: "demoSeed";
  seed?: { program: string; students: number; vacations: number; attendance: number; stats: number; season: number; defaultProgram: string };
}

export interface AddedAdmin {
  club: ClubWorkspace;
  admin: { membershipId: number; name: string; email: string };
  /** True when an existing member of the club was made admin rather than a new one added. */
  promoted: boolean;
  existingAccount: boolean;
  invite: { requested: boolean; status: "sent" | "failed" | "not_requested"; message: string | null };
}

export type ClubDetails = Partial<Pick<ClubWorkspace, "name" | "description" | "address" | "phone" | "email" | "website" | "timezone" | "defaultLanguage" | "mailFromName" | "mailFromAddress">>;

interface Envelope<T> {
  data: T;
  message?: string;
}

const BASE = "/superadmin";

function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

async function get<T>(path: string): Promise<T> {
  return (await apiClient.call<Envelope<T>>(`${BASE}${path}`)).data;
}

async function send<T>(method: "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<Envelope<T>> {
  return apiClient.call<Envelope<T>>(`${BASE}${path}`, { method, body: body === undefined ? undefined : JSON.stringify(body) });
}

/** `count`: every screen displayed. `unique`: a person counts once a day per screen (0 before it was tracked). */
export interface UsageCounts {
  count: number;
  unique: number;
}

export interface UsageStatsDay extends UsageCounts {
  date: string;
  screens: Record<string, number>;
}

/** Screens displayed per day, most recent last; quiet days are present with a zero. */
export interface UsageStats {
  from: string;
  to: string;
  days: UsageStatsDay[];
  screens: ({ screen: string } & UsageCounts)[];
  roles: ({ role: string } & UsageCounts)[];
}

export type QueueName = "default" | "failed";

/** One row of the doctrine transport (messenger_messages). Times are ISO 8601. */
export interface QueueMessage {
  id: string;
  queue: QueueName;
  /** Null when the body no longer decodes and no class could be read from it. */
  class: string | null;
  type: string | null;
  /** The message's properties, one level deep; an email shows its headers only. */
  content: Record<string, unknown> | null;
  enqueuedAt: string | null;
  /** Later than enqueuedAt for a delayed retry. */
  availableAt: string | null;
  /** Set while a worker is handling it. */
  deliveredAt: string | null;
  retries: number;
  error: { class: string; message: string } | null;
  originalTransport: string | null;
  decodable: boolean;
}

export type WorkerStatus = "running" | "stale" | "stopped";

export interface QueueWorker {
  id: string;
  transports: string[];
  status: WorkerStatus;
  startedAt: string | null;
  lastSeenAt: string | null;
  stoppedAt: string | null;
  handled: number;
  failed: number;
  lastMessage: string | null;
  lastMessageAt: string | null;
}

/** A recurring task (cron + message live in code); the operator can pause it or run it now. */
export interface QueueSchedule {
  /** "<schedule>.<message>", the key the endpoints take. */
  id: string;
  schedule: string;
  message: string;
  /** The cron expression. */
  trigger: string;
  nextRunAt: string | null;
  /** The scheduler skips a paused task; "run now" still works. */
  paused: boolean;
  lastRunAt: string | null;
  /** skipped = a run dropped because of the pause; queued = sent by "run now", waiting for a worker. */
  lastStatus: "ok" | "failed" | "skipped" | "queued" | null;
  lastError: string | null;
}

export interface QueueOverview {
  counts: Record<QueueName, number>;
  messages: { items: QueueMessage[]; total: number; page: number; pages: number };
  workers: QueueWorker[];
  schedules: QueueSchedule[];
  /** The server's clock, so elapsed times do not depend on the browser's. */
  serverTime: string;
}

export interface SiteHostDetails {
  platformName: string;
  platformAddress: string;
  platformEmail: string;
  hostName: string;
  hostAddress: string;
  hostPhone: string;
}

export interface ThemeSettings {
  theme: ThemeName;
  themes: ThemeInfo[];
}

export type MediaImageStatus = "ok" | "missing" | "file_missing" | "uncredited";
export type MediaVideoStatus = "ok" | "missing" | "not_applicable";

/** The attribution paragraph of an illustration, with the page it was taken from. */
export interface MediaCredit {
  text: string;
  href: string | null;
}

/** One item of a program template, as the media reviewer sees it. */
export interface MediaItem {
  externalId: string;
  gradeKey: string;
  gradeName: string;
  type: string;
  category: string;
  japaneseName: string;
  translationFr: string;
  position: number;
  imageUrl: string | null;
  videoUrl: string | null;
  credit: MediaCredit | null;
  /** Only techniques (and katas) can carry a video. */
  supportsVideo: boolean;
  imageStatus: MediaImageStatus;
  videoStatus: MediaVideoStatus;
}

export interface MediaGrade {
  key: string;
  name: string;
  color: string;
  icon: string | null;
}

/** What a template covers, and what still asks for attention. */
export interface MediaCoverage {
  items: number;
  techniques: number;
  images: number;
  videos: number;
  videoEligible: number;
  credits: number;
  missingFiles: number;
  withoutImage: number;
  withoutVideo: number;
  techniquesWithoutImage: number;
}

/** A template as the list screen shows it: name, version and coverage. */
export interface MediaProgramSummary extends MediaCoverage {
  key: string;
  name: string;
  discipline: string | null;
  version: number;
  description: string | null;
}

/** One template with its items, ready for review. */
export interface MediaProgram {
  key: string;
  name: string;
  version: number;
  discipline: string | null;
  grades: MediaGrade[];
  items: MediaItem[];
  coverage: MediaCoverage;
  /** Public directory holding the illustrations of this template. */
  mediaDirectory: string;
  /** False when the template file cannot be rewritten (read-only checkout). */
  writable: boolean;
  /** Clubs whose classes run on this template; they only get changes through a sync. */
  usedBy: { id: number; name: string }[];
}

/** A free illustration the open web offers as an alternative. */
export interface MediaCandidate {
  file: string;
  source: "local" | "wikipedia" | "wikipedia-image" | "search";
  reason: string;
  licence: string | null;
  artist: string;
  descurl: string | null;
  thumb: string | null;
  mime: string | null;
  width: number | null;
  height: number | null;
}

export interface MediaCandidates {
  candidates: MediaCandidate[];
  /** How many hits were dropped before reaching the panel, by reason. */
  rejected: { licence: number; subject: number; size: number; format: number };
  terms: string[];
}

/** What a reviewer picked for an item's illustration. */
export type MediaImagePick =
  | { source: "local"; path: string }
  | { source: "commons"; file: string }
  | { source: "upload"; file: File; licence: string; artist: string; sourceUrl: string }
  | { source: "url"; url: string; licence: string; artist: string; sourceUrl: string };

export interface MediaVideoCheck {
  playable: boolean;
  title: string | null;
  authorName: string | null;
  provider: string | null;
}

/** A setup token: what the operator's MCP client authenticates with. Never the secret, except on creation. */
export type SetupTokenState = "active" | "expired" | "revoked";

export interface SetupTokenRow {
  id: number;
  label: string;
  /** Who issued it; any super admin sees and can revoke every token. */
  issuedBy: string;
  issuedByMe: boolean;
  state: SetupTokenState;
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
}

export interface SetupTokenList {
  tokens: SetupTokenRow[];
  /** The MCP endpoint to paste into the client (`claude mcp add`). */
  serverUrl: string;
  defaultDays: number;
  maxDays: number;
}

/** The answer to a creation: the row, plus the secret, which is shown once and never again. */
export interface CreatedSetupToken extends SetupTokenRow {
  token: string;
  serverUrl: string;
}

/** A club's MCP assistant: the operator's switch and the keys bound to that club. */
export interface ClubMcpState {
  enabled: boolean;
  isDemo: boolean;
  keys: SetupTokenRow[];
  /** The club MCP endpoint to paste into the client. */
  serverUrl: string;
  defaultDays: number;
  maxDays: number;
}

export interface CreatedClubKey extends SetupTokenRow {
  token: string;
  serverUrl: string;
}

/** The club's public site as the operator sees it (null when it has none). */
export interface ClubSiteState {
  id: number | null;
  slug: string;
  url: string;
  status: "draft" | "published" | "suspended";
  statusLabel: string;
  suspendedReason: string | null;
  publishedAt: string | null;
  updatedAt: string;
  /** The club's own domain, when it has one. */
  customDomain: {
    host: string;
    status: "pending_dns" | "verifying" | "active" | "failed" | "removing" | null;
    error: string | null;
  } | null;
}

export type ReadinessStatus = "ok" | "warning" | "error" | "skipped";

export type ReadinessKey =
  | "sitesDomain"
  | "dokployEnv"
  | "dokployApi"
  | "wildcardDns"
  | "worker"
  | "siteStorage"
  | "mediaConfig"
  | "mediaBucket"
  | "sentry"
  | "domains";

/** One site whose address is failing or still waiting for its certificate. */
export interface ReadinessSite {
  clubId: number | null;
  clubName: string | null;
  host: string;
  status: "provisioning" | "active" | "failed" | null;
  error: string | null;
  customDomain: string | null;
  customStatus: string | null;
  customError: string | null;
  checkedAt: string | null;
}

export interface ReadinessCheck {
  key: ReadinessKey;
  status: ReadinessStatus;
  /** The facts behind the status, never a secret. `domains` carries `sites`. */
  detail: Record<string, unknown>;
}

export interface SiteReadinessReport {
  /** False as soon as one check is an error: a publish would not reach visitors. */
  ready: boolean;
  checks: ReadinessCheck[];
}

/** One person picked to receive a platform badge: an account, or an email that gets the claim link. */
export interface PlatformRecipient {
  userId?: number;
  email?: string;
  rank?: number | null;
  division?: string | null;
}

export interface PlatformBadgeAwardResult {
  awarded: number;
  awards: number[];
  skipped: { ref: string; reason: string }[];
  badge: ApiBadgeDefinition;
}

export interface PlatformBadgeInput {
  label: string;
  description?: string | null;
  kind?: BadgeKind;
  issuerLabel?: string | null;
  color?: string;
}

function badgeForm(input: Partial<PlatformBadgeInput>, image?: File | null): FormData {
  const form = new FormData();
  if (input.label !== undefined) form.append("label", input.label);
  if (input.description != null) form.append("description", input.description);
  if (input.kind) form.append("kind", input.kind);
  // An empty label is sent on purpose: it clears the issuer.
  if (input.issuerLabel !== undefined) form.append("issuerLabel", input.issuerLabel ?? "");
  if (input.color) form.append("color", input.color);
  if (image) form.append("image", image);
  return form;
}

export const superAdminApi = {
  /** The platform's own badge library (no club), with how many people hold each. */
  platformBadges: () => get<{ badges: ApiBadgeDefinition[] }>("/badges"),

  platformBadge: (id: number) => get<ApiBadgeDefinition & { awards: ApiBadgeAward[] }>(`/badges/${id}`),

  createPlatformBadge: async (input: PlatformBadgeInput, image?: File | null) =>
    (await apiClient.call<Envelope<ApiBadgeDefinition>>(`${BASE}/badges`, { method: "POST", body: badgeForm(input, image) })).data,

  updatePlatformBadge: async (id: number, input: Partial<PlatformBadgeInput>) =>
    (await send<ApiBadgeDefinition>("PATCH", `/badges/${id}`, input)).data,

  setPlatformBadgeImage: async (id: number, image: File) =>
    (await apiClient.call<Envelope<ApiBadgeDefinition>>(`${BASE}/badges/${id}/image`, { method: "POST", body: badgeForm({}, image) })).data,

  duplicatePlatformBadge: async (id: number) => (await send<ApiBadgeDefinition>("POST", `/badges/${id}/duplicate`)).data,
  archivePlatformBadge: async (id: number) => (await send<ApiBadgeDefinition>("POST", `/badges/${id}/archive`)).data,
  unarchivePlatformBadge: async (id: number) => (await send<ApiBadgeDefinition>("POST", `/badges/${id}/unarchive`)).data,
  deletePlatformBadge: async (id: number) => {
    await send<{ id: number }>("DELETE", `/badges/${id}`);
  },

  /** Accounts matching a name or an email (two letters at least). */
  badgeRecipients: (q: string) => get<{ people: { id: number; name: string; email: string }[] }>(`/badge-recipients${query({ q })}`),

  awardPlatformBadge: async (id: number, recipients: PlatformRecipient[]) =>
    (await send<PlatformBadgeAwardResult>("POST", `/badges/${id}/award`, { recipients })).data,

  setPlatformAwardResult: async (awardId: number, result: BadgeResultInput) =>
    (await send<ApiBadgeAward>("PATCH", `/badge-awards/${awardId}/result`, result)).data,

  revokePlatformAward: async (awardId: number, reason: BadgeRevokeReason, note?: string) =>
    (await send<ApiBadgeAward>("POST", `/badge-awards/${awardId}/revoke`, { reason, note })).data,
  clubSite: (clubId: number) => get<ClubSiteState | null>(`/clubs/${clubId}/site`),
  suspendClubSite: async (clubId: number, reason: string) =>
    (await send<ClubSiteState>("POST", `/clubs/${clubId}/site/suspend`, { reason })).data,
  resumeClubSite: async (clubId: number) =>
    (await send<ClubSiteState>("POST", `/clubs/${clubId}/site/resume`)).data,
  checkClubSiteDomain: async (clubId: number) =>
    (await send<ClubSiteState>("POST", `/clubs/${clubId}/site/domain/check`)).data,
  removeClubSiteDomain: async (clubId: number) =>
    (await send<ClubSiteState>("DELETE", `/clubs/${clubId}/site/domain`)).data,

  clubMcp: (clubId: number) => get<ClubMcpState>(`/clubs/${clubId}/mcp`),
  setClubMcp: async (clubId: number, enabled: boolean) => (await send<ClubMcpState>(enabled ? "PUT" : "DELETE", `/clubs/${clubId}/mcp`)).data,
  createClubKey: async (clubId: number, label: string, days: number) =>
    (await send<CreatedClubKey>("POST", `/clubs/${clubId}/mcp/keys`, { label, days })).data,
  revokeClubKey: async (clubId: number, keyId: number) => (await send<SetupTokenRow>("DELETE", `/clubs/${clubId}/mcp/keys/${keyId}`)).data,

  overview: () => get<Overview>("/overview"),

  theme: () => get<ThemeSettings>("/theme"),

  setTheme: async (theme: ThemeName) => (await send<ThemeSettings>("PATCH", "/theme", { theme })).data,

  /** Who runs and hosts the platform: printed on the mentions légales of every club site. */
  siteHost: () => get<SiteHostDetails>("/site-host"),

  setSiteHost: async (input: SiteHostDetails) => (await send<SiteHostDetails>("PUT", "/site-host", input)).data,

  /** Live checks of what a club site publish needs on this server (env, Dokploy, DNS, worker, media, Sentry). */
  siteReadiness: () => get<SiteReadinessReport>("/site-readiness"),

  search: (q: string) => get<SearchResults>(`/search${query({ q })}`),

  listClubs: (params: { q?: string; filter?: "attention"; page?: number; limit?: number }) =>
    get<{ clubs: ClubRow[]; pagination: Pagination }>(`/clubs${query(params)}`),

  club: (id: number | string) => get<ClubWorkspace>(`/clubs/${id}`),

  officialPrograms: () => get<{ programs: ApiOfficialProgramSummary[] }>("/programs/official"),

  createClub: async (club: NewClub) => (await send<CreatedClub>("POST", "/clubs", club)).data,

  /** Runs the creation's checks for one step of the form, writing nothing; throws the refusal the creation would give. */
  checkNewClub: async (club: NewClub, step: "club" | "owner") => {
    await send<{ valid: true }>("POST", "/clubs/check", { ...club, step });
  },

  /** Sends the club logo chosen before the club existed; answers the updated club. */
  uploadClubLogo: async (clubId: number, file: File) => {
    const body = new FormData();
    body.append("logo", file);

    return (await apiClient.call<Envelope<ClubWorkspace>>(`${BASE}/clubs/${clubId}/logo`, { method: "POST", body })).data;
  },

  updateClub: async (id: number, details: ClubDetails) => (await send<ClubWorkspace>("PATCH", `/clubs/${id}`, details)).data,

  /** Opens break-glass access to the club; the session moves there with a club switch afterwards. */
  enterClub: async (id: number, reason: string, minutes: number) =>
    (await send<ClubAccessGrant>("POST", `/clubs/${id}/enter`, { reason, minutes })).data,

  leaveClub: async () => (await send<ClubAccessGrant | null>("POST", "/club-access/leave")).data,

  clubAccessHistory: (clubId?: number) => get<ClubAccessGrant[]>(`/club-access${query({ clubId })}`),

  deleteClub: async (id: number, confirmName: string) => {
    const result = await send<{ clubId: number }>("DELETE", `/clubs/${id}`, { confirmName });
    return { ...result.data, message: result.message ?? "" };
  },

  transferOwnership: async (clubId: number, membershipId: number) => {
    const result = await send<ClubWorkspace>("POST", `/clubs/${clubId}/ownership-transfer`, { membershipId });
    return { workspace: result.data, message: result.message ?? "" };
  },

  addAdmin: async (clubId: number, admin: { firstName: string; lastName: string; email: string; sendInvite: boolean }) => {
    const result = await send<AddedAdmin>("POST", `/clubs/${clubId}/admins`, admin);
    return { ...result.data, message: result.message ?? "" };
  },

  /** Takes an admin out of their club; answers the sentence to show. */
  removeAdmin: async (membershipId: number) => (await send<{ clubId: number }>("POST", `/memberships/${membershipId}/revoke`)).message ?? "",

  updatePerson: async (id: number, changes: { firstName?: string; lastName?: string; email?: string }) => (await send<PersonDetail>("PATCH", `/people/${id}`, changes)).data,

  disablePerson: async (id: number) => (await send<PersonDetail>("POST", `/people/${id}/disable`)).data,

  enablePerson: async (id: number) => (await send<PersonDetail>("POST", `/people/${id}/enable`)).data,

  /** The person asked to restrict the use of their data (Art. 18 GDPR): no emails to or about them. */
  restrictPerson: async (id: number) => (await send<PersonDetail>("POST", `/people/${id}/restrict`)).data,

  unrestrictPerson: async (id: number) => (await send<PersonDetail>("DELETE", `/people/${id}/restrict`)).data,

  /** Deletes the account for good; answers the sentence to show. */
  deletePerson: async (id: number) => (await send<{ id: number }>("DELETE", `/people/${id}`)).message ?? "",

  invite: async (membershipId: number) => (await send<{ email: string | null; invitedAt: string; validHours: number }>("POST", `/memberships/${membershipId}/invite`)).data,

  person: (id: number | string) => get<PersonDetail>(`/people/${id}`),

  usageStats: (params: { days?: number; clubId?: number | string; role?: string }) => get<UsageStats>(`/usage-stats${query(params)}`),

  journal: (params: { clubId?: number | string; action?: string; page?: number; limit?: number }) =>
    get<{ entries: JournalEntry[]; pagination: Pagination }>(`/journal${query(params)}`),

  /** Messenger queues and worker status. */
  queue: (params: { queue?: QueueName; page?: number }) => get<QueueOverview>(`/queue${query(params)}`),

  retryQueueMessage: async (id: string) => (await send<null>("POST", `/queue/failed/${id}/retry`)).message ?? "",

  deleteQueueMessage: async (id: string) => (await send<null>("DELETE", `/queue/failed/${id}`)).message ?? "",

  /** Pauses or resumes a recurring task; answers the tasks as they are now. */
  pauseSchedule: async (id: string, paused: boolean) => (await send<QueueSchedule[]>("PUT", `/queue/schedules/${id}`, { paused })).data,

  /** Sends a recurring task's message through the queue now. */
  runSchedule: async (id: string) => (await send<QueueSchedule[]>("POST", `/queue/schedules/${id}/run`)).data,

  /** People asking for their account to be erased: the ones waiting, or every one with `all`. */
  erasureRequests: (status?: "all") => get<ErasureRequestRow[]>(`/erasure-requests${query({ status })}`),

  /** Erases the account and what is only about the person, then tells them. Cannot be undone. */
  completeErasure: async (id: number) => (await send<{ id: number }>("POST", `/erasure-requests/${id}/complete`)).data,

  /** Keeps the account; the reason is emailed to the person. */
  refuseErasure: async (id: number, note: string) => (await send<{ id: number }>("POST", `/erasure-requests/${id}/refuse`, { note })).data,

  /** The queue of reports members raised from the app. */
  signalements: (params: { status?: string; category?: string; q?: string; page?: number; limit?: number }) =>
    get<SignalementPage>(`/signalements${query(params)}`),

  signalementCounts: async () => (await get<{ counts: SignalementCounts }>("/signalements/summary")).counts,

  updateSignalement: async (id: number, changes: { status?: SignalementStatus; internalNote?: string }) =>
    (await send<SignalementRow>("PATCH", `/signalements/${id}`, changes)).data,

  /** Clubs waiting for a federation's form to be integrated as a blank PDF. */
  pdfRequests: (params: { status?: string; q?: string; page?: number; limit?: number }) =>
    get<PdfIntegrationRequestPage>(`/pdf-integration-requests${query(params)}`),

  pdfRequestCounts: async () => (await get<{ counts: PdfIntegrationRequestCounts }>("/pdf-integration-requests/summary")).counts,

  updatePdfRequest: async (id: number, changes: { status?: PdfIntegrationRequestStatus; note?: string | null }) =>
    (await send<PdfIntegrationRequestRow>("PATCH", `/pdf-integration-requests/${id}`, changes)).data,

  /** The program templates with their media coverage, for the review list. */
  mediaPrograms: () => get<{ programs: MediaProgramSummary[] }>("/media/programs"),

  /** One template with every item, its current media and what is missing. */
  mediaProgram: (key: string) => get<{ program: MediaProgram }>(`/media/programs/${key}`),

  /** Free alternatives for one item, optionally nudged with a search hint. */
  mediaCandidates: (key: string, externalId: string, hint?: string) =>
    get<MediaCandidates>(`/media/programs/${key}/items/${externalId}/candidates${query({ hint })}`),

  /** Publishes an illustration: multipart for an upload, JSON otherwise; answers the updated item. */
  applyMediaImage: async (key: string, externalId: string, pick: MediaImagePick) => {
    const path = `/media/programs/${key}/items/${externalId}/image`;
    if (pick.source === "upload") {
      const body = new FormData();
      body.append("image", pick.file);
      body.append("source", "upload");
      body.append("licence", pick.licence);
      body.append("artist", pick.artist);
      body.append("sourceUrl", pick.sourceUrl);

      return (await apiClient.call<Envelope<{ item: MediaItem }>>(`${BASE}${path}`, { method: "POST", body })).data.item;
    }

    return (await send<{ item: MediaItem }>("POST", path, pick)).data.item;
  },

  /** Takes the illustration off an item; answers the updated item. */
  removeMediaImage: async (key: string, externalId: string) =>
    (await send<{ item: MediaItem }>("DELETE", `/media/programs/${key}/items/${externalId}/image`)).data.item,

  /** Checks a video link before saving it; no write. */
  checkMediaVideo: (url: string) => get<MediaVideoCheck>(`/media/video/check${query({ url })}`),

  setMediaVideo: async (key: string, externalId: string, url: string) =>
    (await send<{ item: MediaItem }>("POST", `/media/programs/${key}/items/${externalId}/video`, { url })).data.item,

  removeMediaVideo: async (key: string, externalId: string) =>
    (await send<{ item: MediaItem }>("DELETE", `/media/programs/${key}/items/${externalId}/video`)).data.item,

  /** The setup tokens the operator's Claude client uses to build clubs. */
  setupTokens: () => get<SetupTokenList>("/setup-tokens"),

  /** Issues one; the answer carries the secret, the only time it is ever readable. */
  createSetupToken: async (label: string, days: number) =>
    (await send<CreatedSetupToken>("POST", "/setup-tokens", { label, days })).data,

  revokeSetupToken: async (id: number) => (await send<SetupTokenRow>("DELETE", `/setup-tokens/${id}`)).data,
};

export { ApiHttpError };
